import { useState, useCallback } from 'react';
import { isValidEmail, validatePassword } from 'Utils/validation';

type FieldError = string | string[] | undefined;

interface UseFormValidationReturn<T extends Record<string, FieldError>> {
  values: Record<keyof T, string>;
  errors: T;
  setFieldValue: (field: keyof T, value: string) => void;
  setFieldError: (field: keyof T, error: T[keyof T]) => void;
  clearFieldError: (field: keyof T) => void;
  validateEmail: (field: keyof T) => boolean;
  validatePassword: (field: keyof T) => boolean;
  validateRequired: (field: keyof T, message?: string) => boolean;
  validateAll: (validations: Array<() => boolean>) => boolean;
  resetForm: () => void;
}

export function useFormValidation<T extends Record<string, FieldError>>(
  initialValues: Record<keyof T, string>,
): UseFormValidationReturn<T> {
  const [values, setValues] = useState<Record<keyof T, string>>(initialValues);
  const [errors, setErrors] = useState<T>({} as T);

  const setFieldValue = useCallback((field: keyof T, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  }, []);

  const setFieldError = useCallback((field: keyof T, error: T[keyof T]) => {
    setErrors((prev) => ({ ...prev, [field]: error }));
  }, []);

  const clearFieldError = useCallback((field: keyof T) => {
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  }, []);

  const validateEmailField = useCallback(
    (field: keyof T): boolean => {
      const value = values[field];
      if (!value.trim()) {
        setFieldError(field, 'Email is required' as T[keyof T]);
        return false;
      }
      if (!isValidEmail(value)) {
        setFieldError(field, 'Please enter a valid email address' as T[keyof T]);
        return false;
      }
      return true;
    },
    [values, setFieldError],
  );

  const validatePasswordField = useCallback(
    (field: keyof T): boolean => {
      const value = values[field];
      if (!value) {
        setFieldError(field, ['Password is required'] as T[keyof T]);
        return false;
      }
      const validation = validatePassword(value);
      if (!validation.isValid) {
        setFieldError(field, validation.errors as T[keyof T]);
        return false;
      }
      return true;
    },
    [values, setFieldError],
  );

  const validateRequired = useCallback(
    (field: keyof T, message = 'This field is required'): boolean => {
      const value = values[field];
      if (!value.trim()) {
        setFieldError(field, message as T[keyof T]);
        return false;
      }
      return true;
    },
    [values, setFieldError],
  );

  const validateAll = useCallback((validations: Array<() => boolean>): boolean => {
    const results = validations.map((validate) => validate());
    return results.every(Boolean);
  }, []);

  const resetForm = useCallback(() => {
    setValues(initialValues);
    setErrors({} as T);
  }, [initialValues]);

  return {
    values,
    errors,
    setFieldValue,
    setFieldError,
    clearFieldError,
    validateEmail: validateEmailField,
    validatePassword: validatePasswordField,
    validateRequired,
    validateAll,
    resetForm,
  };
}
